import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import { ChevronRightRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  useGetClientCampaignDetailQuery,
  useGetClientOverviewQuery,
} from "../../store/apis/clientPortalApi";
import DetailSectionsViewClient, {
  BarList,
} from "../../components/client/DetailSectionsViewClient";
import {
  ClientCard,
  ClientHeader,
  ClientPage,
  ClientTrail,
  PlatformTag,
  ProgressBar,
  StatusPill,
} from "../../components/client/ClientUiViewClient";
import {
  ClientError,
  ClientMessage,
} from "../../components/client/ClientStatesViewClient";
import DetailSkeletonViewClient, {
  DetailSectionsLoading,
} from "../../components/client/DetailSkeletonViewClient";
import MetricChartsViewClient, {
  buildMetricDays,
  goalInRange,
  lifetimeTotals,
  sumDays,
} from "../../components/client/MetricChartsViewClient";
import {
  DateRange,
  DateRangeBar,
  LIFETIME,
  resolveRange,
} from "../../components/client/DateRangePickerViewClient";
import {
  moneyStatItems,
  splitMoneyItems,
} from "../../components/client/MoneyViewClient";
import {
  GoalPanel,
  StatPanels,
} from "../../components/client/StatPanelsViewClient";
import StickyStatsViewClient, {
  performanceStatItems,
} from "../../components/client/StickyStatsViewClient";
import {
  ROW_ATTR,
  TITLE_SOURCE_ATTR,
  useClientTransition,
} from "../../components/client/clientTransitionViewClient";
import {
  formatCompact,
  formatNumber,
  formatDate,
  formatMoney,
  formatPercent,
  subcampaignPath,
} from "../../components/client/clientFormatViewClient";

const SUB_GRID = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 130px 160px 90px 80px 110px 132px 24px",
  alignItems: "center",
  columnGap: 2,
} as const;

/** Una campaña: cómo va, su gráfica y sus subcampañas. */
const CampaignViewClient: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const theme = useTheme();
  const go = useClientTransition();
  const { data, isLoading, isError, refetch } = useGetClientOverviewQuery();
  // El detalle llega aparte: la página se pinta ya y sus apartados aparecen después.
  const { data: detail, isLoading: detailLoading } =
    useGetClientCampaignDetailQuery(campaignId ?? "", { skip: !campaignId });
  // Las fechas de arriba: todo lo de la página es de ese rango.
  const [range, setRange] = useState<DateRange>(LIFETIME);

  if (isLoading) return <DetailSkeletonViewClient withSubcampaigns />;
  if (isError || !data) return <ClientError onRetry={refetch} />;

  const campaign = data.campaigns.find((c) => c.id === campaignId);
  if (!campaign)
    return (
      <ClientMessage
        title={t("clientView.campaign.notFound")}
        action={{ label: t("clientView.home"), to: "/" }}
      />
    );

  const subs = campaign.subcampaigns;
  const asOf = data.asOfDate;
  const days = buildMetricDays(subs, campaign.kpi, asOf, detail?.daily);
  const bounds = {
    min: days[0]?.date ?? asOf,
    max: days[days.length - 1]?.date ?? asOf,
  };
  const { from: dateFrom, to: dateTo } = resolveRange(range, bounds, asOf);
  const isLifetime = range.preset === "lifetime";
  const inRange = isLifetime
    ? lifetimeTotals(subs, sumDays(days, dateFrom, dateTo))
    : sumDays(days, dateFrom, dateTo);
  // Cada subcampaña, en esas fechas (del resumen: impresiones, clics,
  // inversión y su objetivo).
  const subDays = new Map(
    subs.map((s) => [s.id, buildMetricDays([s], s.kpi, asOf)])
  );
  const subTotals = (id: string) =>
    sumDays(subDays.get(id) ?? [], dateFrom, dateTo);
  const subProgress = (sub: (typeof subs)[number]) => {
    const goal = goalInRange(
      subDays.get(sub.id) ?? [],
      sub.kpi,
      dateFrom,
      dateTo
    );
    return goal.planned ? goal.delivered / goal.planned : null;
  };
  const withImpressions = subs.filter((s) => subTotals(s.id).impressions > 0);
  // Objetivo de la campaña solo con las subcampañas con plan que miden lo
  // mismo que ella, en esas fechas.
  const planned = subs.filter((s) => s.planned && s.kpi === campaign.kpi);
  const goal = planned.length
    ? goalInRange(
        buildMetricDays(planned, campaign.kpi, asOf),
        campaign.kpi,
        dateFrom,
        dateTo
      )
    : null;
  const kpiLabel = t(`clientView.kpi.${campaign.kpi}`);
  // Las subcampañas son el camino para bajar de nivel: su panel va en azul.
  const blue = theme.palette.info.main;
  // El objetivo va en su panel; en el panel fijo, con el resto de cifras.
  const performance = performanceStatItems(
    inRange,
    t,
    language,
    goal?.planned
      ? { delivered: goal.delivered, planned: goal.planned, kpiLabel }
      : undefined
  );
  // El presupuesto de esas fechas: lo previsto en ellas, si lo tienen todas.
  const totals = {
    ...inRange,
    budget: subs.every((s) => s.budget) ? inRange.plannedInvestment : null,
  };
  const money = splitMoneyItems(
    moneyStatItems(totals, campaign.currency, t, language)
  );

  return (
    <ClientPage>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <ClientTrail
          items={[
            { label: t("clientView.home"), to: "/", returnTo: campaign.id },
            { label: campaign.name },
          ]}
        />
        <ClientHeader title={campaign.name}>
          <PlatformTag platform={campaign.platform} />
          <StatusPill status={campaign.status} />
          <span>
            {formatDate(campaign.startDate, language)} –{" "}
            {formatDate(campaign.endDate, language)}
          </span>
          <span>{t("clientView.goalIs", { kpi: kpiLabel })}</span>
        </ClientHeader>
      </Box>

      <DateRangeBar
        value={range}
        onChange={setRange}
        bounds={bounds}
        yesterday={asOf}
      />

      {goal?.planned && <GoalPanel goal={goal} kpiLabel={kpiLabel} />}

      <StickyStatsViewClient groups={[performance, money.budget, money.costs]}>
        <StatPanels
          results={performance.filter((item) => item.key !== "goalReached")}
          investment={totals.investment}
          budget={totals.budget}
          currency={campaign.currency}
          costs={money.costs}
        />
      </StickyStatsViewClient>

      <MetricChartsViewClient
        days={days}
        kpi={campaign.kpi}
        currency={campaign.currency}
        from={dateFrom}
        to={dateTo}
      />

      <ClientCard
        title={t("clientView.campaign.subcampaigns")}
        sx={{
          backgroundColor: alpha(blue, 0.08),
          borderColor: alpha(blue, 0.3),
        }}
      >
        {subs.length === 0 ? (
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            {t("clientView.table.noSubcampaigns")}
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto", mx: { xs: -2.5, md: -3.5 } }}>
            <Box sx={{ minWidth: 960, px: { xs: 2.5, md: 3.5 } }}>
              <Box sx={{ ...SUB_GRID, px: 2.5, pb: 2 }}>
                {[
                  t("clientView.table.subcampaign"),
                  t("clientView.table.platform"),
                  t("clientView.table.goal"),
                  t("clientView.table.impressions"),
                  t("clientView.table.clicks"),
                  t("clientView.table.investment"),
                  t("clientView.table.status"),
                ].map((label) => (
                  <Typography
                    key={label}
                    component="span"
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "text.secondary",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                    }}
                  >
                    {label}
                  </Typography>
                ))}
                <span />
              </Box>
              {subs.map((sub) => {
                const progress = subProgress(sub);
                const subInRange = subTotals(sub.id);
                const to = subcampaignPath(campaign.id, sub.id);
                return (
                  <Box
                    key={sub.id}
                    role="link"
                    {...{ [ROW_ATTR]: sub.id }}
                    tabIndex={0}
                    onClick={(e) => go(to, { from: e.currentTarget })}
                    onKeyDown={(e) =>
                      e.key === "Enter" && go(to, { from: e.currentTarget })
                    }
                    sx={{
                      ...SUB_GRID,
                      px: 2.5,
                      py: 1.5,
                      borderTop: `1px solid ${alpha(blue, 0.2)}`,
                      cursor: "pointer",
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        backgroundColor: alpha(blue, 0.16),
                        borderRadius: "16px",
                      },
                    }}
                  >
                    <Typography
                      title={sub.name}
                      {...{ [TITLE_SOURCE_ATTR]: "" }}
                      sx={{
                        justifySelf: "start",
                        maxWidth: "100%",
                        fontSize: 14,
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sub.name}
                    </Typography>
                    <PlatformTag platform={sub.platform} />
                    {progress !== null ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <ProgressBar value={progress} />
                        </Box>
                        <Typography
                          sx={{ fontSize: 12, fontWeight: 700, minWidth: 40 }}
                        >
                          {formatPercent(progress, language)}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        sx={{ fontSize: 13, color: "text.secondary" }}
                      >
                        {t(`clientView.kpi.${sub.kpi}`)}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 13 }}>
                      {formatNumber(subInRange.impressions, language)}
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      {formatCompact(subInRange.clicks, language)}
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      {formatMoney(
                        subInRange.investment,
                        campaign.currency,
                        language
                      )}
                    </Typography>
                    <Box>
                      <StatusPill status={sub.status} />
                    </Box>
                    <ChevronRightRounded sx={{ color: "text.secondary" }} />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </ClientCard>

      {withImpressions.length > 1 && (
        <ClientCard
          title={t("clientView.detail.splitTitle")}
          subtitle={t("clientView.detail.splitSubtitle")}
        >
          <BarList
            initial={8}
            rows={withImpressions
              .map((s) => ({ s, totals: subTotals(s.id) }))
              .sort((a, b) => b.totals.impressions - a.totals.impressions)
              .map(({ s, totals: t }) => ({
                key: s.id,
                label: s.name,
                impressions: t.impressions,
                clicks: t.clicks,
                investment: t.investment,
              }))}
            currency={campaign.currency}
          />
        </ClientCard>
      )}

      {detailLoading ? (
        <DetailSectionsLoading />
      ) : (
        detail && (
          <DetailSectionsViewClient
            detail={detail}
            currency={campaign.currency}
            from={dateFrom}
            to={dateTo}
            lifetime={range.preset === "lifetime"}
          />
        )
      )}
    </ClientPage>
  );
};

export default CampaignViewClient;
